import {
  BaseMessageSignerWalletAdapter,
  type SupportedTransactionVersions,
  type SendTransactionOptions,
  WalletName,
  WalletReadyState,
} from '@solana/wallet-adapter-base';
import type {
  Connection,
  TransactionSignature,
  Transaction,
  PublicKeyInitData,
} from '@solana/web3.js';
import { PublicKey, Transaction as LegacyTx, VersionedTransaction } from '@solana/web3.js';
import { getWallets } from '@wallet-standard/app';
import type { Wallet, WalletAccount } from '@wallet-standard/base';
import type {
  SolanaSignMessageOutput,
  SolanaSignAndSendTransactionOutput,
  SolanaSignTransactionOutput,
  SolanaSignMessageMethod,
  SolanaSignTransactionMethod,
  SolanaSignAndSendTransactionMethod,
} from '@solana/wallet-standard-features';
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';

export type SolanaChainId = 'solana:mainnet' | 'solana:devnet' | 'solana:testnet';

function toTxSignature(sig: unknown): TransactionSignature {
  if (typeof sig === 'string') return sig as TransactionSignature;
  if (sig instanceof Uint8Array) return bs58.encode(sig);
  if (Array.isArray(sig)) return bs58.encode(Uint8Array.from(sig as any));
  throw new Error('Unsupported signature format from wallet');
}

export function toSolanaChainId(network: 'mainnet-beta' | 'devnet' | 'testnet'): SolanaChainId {
  if (network === 'mainnet-beta') return 'solana:mainnet';
  if (network === 'devnet') return 'solana:devnet';
  return 'solana:testnet';
}

function unwrapOne<T>(out: T | readonly T[]): T {
  return Array.isArray(out) ? out[0] : out;
}

function deserializeAny(bytes: Uint8Array): Transaction | VersionedTransaction {
  try {
    return VersionedTransaction.deserialize(bytes);
  } catch {}
  // legacy
  return LegacyTx.from(bytes);
}

export class BackpackStandardAdapter extends BaseMessageSignerWalletAdapter {
  name = 'Backpack' as WalletName<'Backpack'>;
  url = 'https://backpack.app';
  icon: string | undefined;

  public supportedTransactionVersions: SupportedTransactionVersions = null;
  public connecting = false;

  private wallet?: Wallet;
  private account?: WalletAccount;
  public publicKey: PublicKey;
  private chain?: SolanaChainId;

  constructor(opts?: { network: 'mainnet-beta' | 'devnet' | 'testnet' }) {
    super();
    this.chain = toSolanaChainId(opts?.network);
  }

  get readyState() {
    const w = getWallets().get().find(w => w.name.toLowerCase().includes('backpack'));
    return w ? WalletReadyState.Installed : WalletReadyState.NotDetected;
  }

  async connect(): Promise<void> {
    if (this.connecting) return;
    this.connecting = true;
    try {
      const api = getWallets();
      const w = api.get().find(w => w.name.toLowerCase().includes('backpack'));
      if (!w) throw new Error('Backpack not found');

      this.wallet = w;
      const connectFeat = w.features['standard:connect'] as { connect: () => Promise<{ accounts: WalletAccount[] }> } | undefined;
      if (!connectFeat) throw new Error('standard:connect not supported');

      const { accounts } = await connectFeat.connect();
      if (!accounts?.length) throw new Error('No accounts returned from Backpack');

      this.account = accounts[0];
      this.publicKey = new PublicKey(this.account.address as PublicKeyInitData);
      this.emit('connect', this.publicKey);
    } finally {
      this.connecting = false;
    }
  }

  async disconnect(): Promise<void> {
    try {
      const df = this.wallet?.features['standard:disconnect'] as { disconnect?: () => Promise<void> } | undefined;
      if (df?.disconnect) await df.disconnect();
    } finally {
      this.account = undefined;
      this.publicKey = undefined;
      this.wallet = undefined;
      this.emit('disconnect');
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    if (!this.wallet || !this.account) throw new Error('Not connected');
    const feat = this.wallet.features['solana:signMessage'] as { signMessage: SolanaSignMessageMethod } | undefined;
    if (!feat) throw new Error('signMessage not supported');
    const out: SolanaSignMessageOutput | readonly SolanaSignMessageOutput[] =
      await feat.signMessage({ account: this.account, message });
    const one = unwrapOne(out);
    return one.signature as Uint8Array;
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(tx: T): Promise<T> {
    if (!this.wallet || !this.account) throw new Error('Not connected');
    const feat = this.wallet.features['solana:signTransaction'] as { signTransaction: SolanaSignTransactionMethod } | undefined;
    if (!feat) throw new Error('signTransaction not supported by Backpack');

    const serialized = (tx as VersionedTransaction | Transaction).serialize();
    const out: SolanaSignTransactionOutput | readonly SolanaSignTransactionOutput[] =
      await feat.signTransaction({ account: this.account, transaction: new Uint8Array(serialized), chain: this.chain });

    const one = unwrapOne(out);
    const bytes = one.signedTransaction as Uint8Array;
    return deserializeAny(bytes) as T;
  }

  async sendTransaction(
    transaction: Transaction | VersionedTransaction,
    connection: Connection,
    _options?: SendTransactionOptions
  ): Promise<TransactionSignature> {
    if (!this.wallet || !this.account) throw new Error('Not connected');

    const signAndSend = this.wallet.features['solana:signAndSendTransaction'] as { signAndSendTransaction: SolanaSignAndSendTransactionMethod } | undefined;
    if (signAndSend) {
      const serialized = transaction.serialize();
      const out: SolanaSignAndSendTransactionOutput | readonly SolanaSignAndSendTransactionOutput[] =
        await signAndSend.signAndSendTransaction({
          account: this.account,
          transaction: new Uint8Array(serialized),
          chain: this.chain,
        });
      const one = unwrapOne(out);
      return toTxSignature(one.signature);
    }

    const signed = await this.signTransaction(transaction);
    const sig = await connection.sendRawTransaction(signed.serialize(), { skipPreflight: false });
    return sig;
  }
}
